// SalesOrderRecord/components/SalesOrderRecordFilters.js
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { Card, Row, Col, DatePicker, Button } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { setFilters } from "../SalesOrderRecordSlice";
import { fetchSalesOrderRecord } from "../SalesOrderRecordThunk";
import Swal from "sweetalert2";
import { currentDateStartTime, currentDateEndTime, dateTimeFormat } from "../../../../../lib/functions";
import { useDefaultDateRange } from "../../../../../hooks/useDefaultDateRange";
import './style.css'

import SearchListAutoComplete from "../../../../../components/SearchListAutoComplete";
import StaticAutoComplete from "../../../../../components/StaticAutoComplete";
import RecordDateFilter from "../../../../../components/filters/RecordDateFilter";

import {
  fetchEmployees,
  fetchCustomers,
  fetchItems,
  fetchGroups,
  fetchCategories,
  fetchUsers,
  fetchTagGroups,
  fetchTagGroupFields,
  fetchLocations,
  fetchItemSerials
} from "../../../../../store/commonThunks/lookups";
import CommonDateRangeFilter from "../../../../../components/common/filters/CommonDateRangeFilter";


const SalesOrderRecordFilters = ({ messageApi }) => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.salesOrderRecord);
  const lookups = useSelector((state) => state.lookups);

  useDefaultDateRange(setFilters, filters, "thisMonth");   // 👈 defaults to today //default date using custom hook

  // quick range handler
  const setQuickRange = (range) => {
    const today = dayjs();
    let fromDate, toDate;

    switch (range) {
      case "last7days":
        fromDate = currentDateStartTime(today.subtract(6, "day"));
        toDate = currentDateEndTime(today);
        break;
      case "thisMonth":
        fromDate = currentDateStartTime(today.startOf("month"));
        toDate = currentDateEndTime(today.endOf("month"));
        break;
      default: // today
        fromDate = currentDateStartTime(today);
        toDate = currentDateEndTime(today);
    }

    handleFilterChange("fromDate", fromDate);
    handleFilterChange("toDate", toDate);
  };

  // fetch lookup data on mount
  useEffect(() => {
    dispatch(fetchEmployees());
    dispatch(fetchGroups());
    dispatch(fetchCustomers());
    dispatch(fetchItems());
    dispatch(fetchCategories());
    dispatch(fetchUsers());
    dispatch(fetchTagGroups());
    dispatch(fetchLocations());
  }, [dispatch]);

  // Auto run report once defaults are in place
  useEffect(() => {
    if (filters.filterType && filters.recordType && filters.fromDate && filters.toDate) {
      handleReport();
    }
  }, [filters]);


  const dynamicSpan = filters.filterType === "By Tag" ? 3 : 4

  // useEffect(() => {
  //   // On the first load, if dates are not set, set them to today.
  //   if (!filters.fromDate && !filters.toDate) {
  //     const today = dayjs();
  //     dispatch(setFilters({
  //       fromDate: today,
  //       toDate: today,
  //     }));
  //   }
  // }, [dispatch, filters.fromDate, filters.toDate]);

  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleReport = () => {
    if (!filters.filterType) {
      messageApi.warning("Select Filter Type");
      return;
    }

    const payload = {
      selectedFilterType: filters.filterType,
      selectedRecordType: filters.recordType,
      filters: {
        ...filters,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      }
    };

    dispatch(fetchSalesOrderRecord(payload)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        messageApi.success("Report loaded ✅");
      } else {
        messageApi.error("Failed to fetch records ❌");
      }
    });
  };


  // 🔹 dynamic extra filter fields
  const renderExtraField = () => {
    switch (filters.filterType) {
      case "By Employee":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search Employee</label>
            <SearchListAutoComplete
              entityType="employee"
              optionsList={lookups.employees.map((e) => ({
                sale_id: e.employee_id,
                display_text: e.employee_name,
              }))}
              onSelect={(obj) => handleFilterChange("employeeId", obj?.sale_id)} // ✅ use sale_id
              placeholder="Search Employee"
              showAddNew={true}
            />
          </Col>
        );

      case "By Customer":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search Customer</label>
            <SearchListAutoComplete
              entityType="customer"
              optionsList={lookups.customers.map((e) => ({
                sale_id: e.acc_id,
                display_text: e.acc_name,
              }))}
              onSelect={(obj) => handleFilterChange("customerId", obj?.sale_id)}
              placeholder="Customer"
              showAddNew={true}
            />
          </Col>
        );

      case "By Item":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search Item</label>
            <SearchListAutoComplete
              entityType="item"
              optionsList={lookups.items.map((e) => ({
                sale_id: e.item_id,
                display_text: e.item_name,
              }))}
              onSelect={(obj) => handleFilterChange("itemId", obj?.sale_id)}
              placeholder="Item"
              showAddNew={true}
            />

          </Col>
        );

      case "By Group":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search Group</label>
            <SearchListAutoComplete
              entityType="group"
              optionsList={lookups.groups.map((e) => ({
                sale_id: e.group_id,
                display_text: e.group_name,
              }))}
              onSelect={(obj) => handleFilterChange("groupId", obj?.sale_id)} // ✅ use sale_id
              placeholder="Group"
              showAddNew={true}
            />
          </Col>
        );

      case "By Category":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search Category</label>
            <SearchListAutoComplete
              entityType="category"
              optionsList={lookups.categories.map((e) => ({
                sale_id: e.category_id,
                display_text: e.category_name,
              }))}
              onSelect={(obj) => handleFilterChange("categoryId", obj?.sale_id)}
              placeholder="Category"
              showAddNew={true}
            />
          </Col>
        );

      case "By User":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search User</label>
            <SearchListAutoComplete
              entityType="user"
              optionsList={lookups.users.map((u) => ({
                sale_id: u.id,
                display_text: u.full_name,
              }))}
              onSelect={(obj) => handleFilterChange("userId", obj?.sale_id)}
              placeholder="User"
              showAddNew={true}
            />
          </Col>
        );

      case "By Tag":
        return (
          <>
            <Col xs={24} sm={12} md={8} xl={5}>
              <label>Tag Group</label>
              <SearchListAutoComplete
                optionsList={lookups.tagGroups.map((tg) => ({
                  sale_id: tg.id,
                  display_text: tg.tag_groupname,
                }))}
                onSelect={(obj) => {
                  handleFilterChange("tag_groupnameid", obj?.sale_id);
                  if (obj?.sale_id) dispatch(fetchTagGroupFields(obj.sale_id)); // ✅ just pass id
                }}
                placeholder="Tag Group"
              />
            </Col>
            <Col xs={24} sm={12} md={8} xl={5}>
              <label>Tag Field</label>
              <SearchListAutoComplete
                optionsList={lookups.tagFields.map((tf) => ({
                  sale_id: tf.id,
                  display_text: tf.display_text || tf.combinedField, // ✅ consistent naming
                }))}
                onSelect={(obj) => handleFilterChange("tagFeildNameid", obj?.sale_id)}
                placeholder="Tag Field"
              />

            </Col>
          </>
        );

      // case "By Item Serial":
      // return (
      //   <Col span={6}>
      //     <label>Search Item Serial</label>
      //     <SearchAutoComplete
      //       searchThunk={(text) => dispatch(fetchItems(text))} // ⚠️ If you have a dedicated fetchItemSerials thunk, use that
      //       resultSelector={(state) => state.lookups.items}
      //       onSelect={(obj) => handleFilterChange("itemSerial", obj?.serial_no)} // adapt to your API field
      //       placeholder="Item Serial"
      //     />
      //   </Col>
      // );

      case "By Location":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search Location</label>
            <SearchListAutoComplete
              optionsList={lookups?.locations?.map((loc) => ({
                sale_id: loc.location_id,
                display_text: loc.location_name,
              }))}
              onSelect={(obj) => handleFilterChange("locationId", obj?.sale_id)}
              placeholder="Location"
            />
          </Col>
        );


      default:
        return null;
    }
  };

  return (
    <Card className="product-replace-filters mb-4 shadow-md rounded-xl">
      <Row gutter={[16, 16]} wrap style={{ alignItems: "center" }}>
        <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4} >
          <label>Filter Type</label>
          <SearchListAutoComplete
            optionsList={[
              { sale_id: "All", display_text: "All" },
              { sale_id: "By Customer", display_text: "By Customer" },
              { sale_id: "By Condition Sale", display_text: "By Condition Sale" },
              // { sale_id: "By Employee", display_text: "By Employee" },
              { sale_id: "By Item", display_text: "By Item" },
              { sale_id: "By Tag", display_text: "By Tag" },
              { sale_id: "By Group", display_text: "By Group" },
              { sale_id: "By Category", display_text: "By Category" },
              { sale_id: "By Item Serial", display_text: "By Item Serial" },
              { sale_id: "By Location", display_text: "By Location" },
              { sale_id: "By User", display_text: "By User" },
            ]}
            value={{
              value: filters.filterType,
              label: filters.filterType,
            }}
            onSelect={(obj) => handleFilterChange("filterType", obj?.sale_id)}
            placeholder="Filter Type"
            showAddNew={false}
          />
        </Col>

        {renderExtraField()}

        <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4} >
          <label>Record Type</label>
          <SearchListAutoComplete
            optionsList={[
              { sale_id: "with item", display_text: "With Item" },
              { sale_id: "without item", display_text: "Without Item" },
            ]}
            value={
              filters.recordType
                ? { value: filters.recordType, label: filters.recordType === "with item"? "With Item" : "Without Item"}
                : undefined
            }
            onSelect={(obj) => handleFilterChange("recordType", obj?.sale_id)}
            placeholder="Record Type"
            showAddNew={false}
          />
        </Col>

        <CommonDateRangeFilter
          filters={filters}
          onChange={(fromDate, toDate) => {
            handleFilterChange("fromDate", fromDate);
            handleFilterChange("toDate", toDate);
          }}
        />

        <Col
          xs={24} sm={12} md={9} xl={3}
          style={{ paddingTop: "1.4rem" }}>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleReport}
            style={{height: 35.19}}
            block
          >
            Report
          </Button>
        </Col>
      </Row>
      <style>{`
    @media (min-width: 768px) and (max-width: 1024px) {
      .product-replace-filters .ant-row .ant-col {
        flex: 0 0 50% !important;
        max-width: 50% !important;
      }
    }
  `}</style>
    </Card>
  );
};

export default SalesOrderRecordFilters;
